import { useId, useState } from "react";
import { useMutation } from "convex/react";
import {
  estSlugValide,
  slugDepuisTitre,
  type ContenuScorecardSaisi,
} from "@repo/ent-struct";
import {
  Button,
  Dialog,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from "@repo/ui";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { messageErreur } from "./edition";
import { lireContenuJson } from "./json";

/** Où créer : une organisation (sans ID : celle de l'utilisateur) ou le catalogue. */
export type CibleCreation =
  | { type: "organisation"; organisationId?: Id<"organisations"> }
  | { type: "modele" };

type Depart = "vierge" | "json";

function compterQuestions(contenu: ContenuScorecardSaisi): number {
  return contenu.competences.reduce(
    (total, c) =>
      total +
      (c.questions?.length ?? 0) +
      (c.sousCompetences ?? []).reduce((t, s) => t + s.questions.length, 0),
    0,
  );
}

/**
 * Création d'une scorecard, vierge ou depuis un JSON. Elle naît en brouillon :
 * on la complète dans l'éditeur, puis on la publie pour l'utiliser en campagne.
 *
 * `saisirSlug` (super-admin) : le slug est proposé d'après le titre et
 * modifiable ; sinon le serveur le génère.
 */
export function NouvelleScorecardDialog({
  cible,
  saisirSlug,
  onFermer,
  onCreee,
}: {
  cible: CibleCreation;
  saisirSlug: boolean;
  onFermer: () => void;
  onCreee: (id: Id<"scorecards">) => void;
}) {
  const ids = { titre: useId(), slug: useId(), json: useId(), fichier: useId() };
  const creer = useMutation(api.scorecards.creerScorecard);
  const [depart, setDepart] = useState<Depart>("vierge");
  const [titre, setTitre] = useState("");
  const [slugSaisi, setSlugSaisi] = useState<string | null>(null);
  const [texteJson, setTexteJson] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // Le slug suit le titre tant que l'utilisateur ne l'a pas touché.
  const slug = slugSaisi ?? slugDepuisTitre(titre);
  const lecture = depart === "json" && texteJson.trim() ? lireContenuJson(texteJson) : null;
  const importe = lecture?.ok ? lecture.contenu : null;

  const saisirJson = (texte: string) => {
    setTexteJson(texte);
    const lu = lireContenuJson(texte);
    // Le titre du fichier sert de proposition, sans écraser une saisie.
    if (lu.ok && !titre.trim()) setTitre(lu.contenu.titre);
  };

  const chargerFichier = async (fichier: File | undefined) => {
    if (fichier) saisirJson(await fichier.text());
  };

  const pret =
    titre.trim() !== "" &&
    (!saisirSlug || estSlugValide(slug)) &&
    (depart === "vierge" || importe !== null);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      onCreee(
        await creer({
          cible,
          titre,
          ...(saisirSlug && { slug }),
          ...(importe && {
            description: importe.description,
            competences: importe.competences,
          }),
        }),
      );
    } catch (e) {
      setErreur(messageErreur(e));
      setEnCours(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onFermer}
      title={cible.type === "modele" ? "Nouveau modèle" : "Nouvelle scorecard"}
      className="max-w-2xl"
    >
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (pret) void valider();
        }}
      >
        <p className="text-sm text-muted-foreground">
          Elle est créée en brouillon : complétez-la dans l'éditeur, puis
          publiez-la pour la proposer aux campagnes.
        </p>

        <RadioGroup
          value={depart}
          onValueChange={(v) => setDepart(v as Depart)}
          className="flex gap-6"
          aria-label="Point de départ"
        >
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="vierge" /> Scorecard vierge
          </label>
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="json" /> Importer un JSON
          </label>
        </RadioGroup>

        {depart === "json" && (
          <div className="space-y-2">
            <Label htmlFor={ids.fichier}>Fichier JSON</Label>
            <Input
              id={ids.fichier}
              type="file"
              accept="application/json,.json"
              onChange={(e) => void chargerFichier(e.target.files?.[0])}
            />
            <Label htmlFor={ids.json} className="text-xs text-muted-foreground">
              … ou collez son contenu
            </Label>
            <Textarea
              id={ids.json}
              spellCheck={false}
              className="min-h-40 font-mono text-xs"
              value={texteJson}
              aria-invalid={lecture?.ok === false || undefined}
              onChange={(e) => saisirJson(e.target.value)}
            />
            {lecture?.ok === false && (
              <p role="alert" className="text-sm text-destructive">
                {lecture.erreur}
              </p>
            )}
            {importe && (
              <p className="text-sm text-muted-foreground">
                {importe.competences.length} compétence
                {importe.competences.length > 1 ? "s" : ""}, {compterQuestions(importe)}{" "}
                question{compterQuestions(importe) > 1 ? "s" : ""}. Les IDs valides du
                fichier sont conservés, les autres sont attribués.
              </p>
            )}
          </div>
        )}

        <div>
          <Label htmlFor={ids.titre} className="mb-1">
            Titre
          </Label>
          <Input id={ids.titre} value={titre} onChange={(e) => setTitre(e.target.value)} />
        </div>

        {saisirSlug && (
          <div>
            <Label htmlFor={ids.slug} className="mb-1">
              Slug (identifiant technique, définitif)
            </Label>
            {/* Pas de `font-mono` : sous Linux, le « _ » de la police monospace
                dépasse la zone de texte du champ et Chrome le rogne. */}
            <Input
              id={ids.slug}
              value={slug}
              aria-invalid={(titre !== "" && !estSlugValide(slug)) || undefined}
              onChange={(e) => setSlugSaisi(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Minuscules et chiffres, séparés par « - » ou « _ ». Ne pourra plus
              être modifié.
            </p>
          </div>
        )}

        {erreur && (
          <p role="alert" className="text-sm text-destructive">
            {erreur}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onFermer}>
            Annuler
          </Button>
          <Button type="submit" disabled={!pret || enCours}>
            Créer le brouillon
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
